import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { DeviceService } from '../_services/device.service';
import { WSService } from '../_services/ws.service';
import { environment } from 'src/environments/environment';

/**
 * Shared device info bar — the bottom-right "id / version / fullscreen" strip used on every
 * eMeni app shell (visible on the login screen too), pinned to the viewport with position:fixed.
 *
 * The settings gear is hidden by default and revealed when ANY of:
 *   - the server pushes a WS "config" message with showSettingsButton (auto-hides after ~10s),
 *   - [devMode] is true (apps pass !environment.production so devs always have access),
 *   - [adminUnlocked] is true (app-specific admin unlock, e.g. Kiosk's barcode admin mode).
 * The host app handles (openSettings) with its own action — a dialog or a route.
 */
@Component({
    selector: 'app-device-info-bar',
    templateUrl: './device-info-bar.component.html',
    styleUrls: ['./device-info-bar.component.scss'],
    standalone: false
})
export class DeviceInfoBarComponent implements OnInit, OnDestroy {
  /** Apps pass !environment.production so the gear stays available in dev/test builds. */
  @Input() devMode = false;
  /** App-specific admin unlock (e.g. Kiosk's AdminModeService.isActive). */
  @Input() adminUnlocked = false;
  @Output() openSettings = new EventEmitter<void>();
  environment = environment;

  deviceID = '';
  private showByConfig = false;
  private destroyed = new Subject<void>();

  get showSettings(): boolean {
    return this.showByConfig || this.devMode || this.adminUnlocked;
  }

  constructor(private deviceService: DeviceService, private ws: WSService) { }

  ngOnInit(): void {
    this.deviceService.getID()
      .pipe(takeUntil(this.destroyed))
      .subscribe(id => { this.deviceID = id != null ? id.toString() : ''; });

    // The server reveals the settings gear for ~10s via a WS "config" message.
    this.ws.observe('config')
      .pipe(takeUntil(this.destroyed))
      .subscribe((msg: any) => {
        this.showByConfig = !!msg?.data?.showSettingsButton;
        if (this.showByConfig) {
          setTimeout(() => { this.showByConfig = false; }, 10000);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
